import { FormattedNumber } from 'react-intl';
import styled, { css } from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BrandM,
  BrandXS,
  HeadlineL,
  LabelS,
  LabelXS,
} from '@sorare/core/src/atoms/typography';
import { Ordinal } from '@sorare/core/src/atoms/typography/Ordinal';
import { TrophyIcon } from '@sorare/core/src/components/club/Trophies';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { WithRewardRange } from '@sorare/core/src/lib/rewards';

import { LineupRewardsDistanceLabel } from 'components/lineup/LineupRewardsDistanceLabel';

const gradientText = css`
  background: linear-gradient(
    323deg,
    var(--from-gradient-color) 14.3%,
    var(--to-gradient-color) 85.7%
  );
  background-size: 100%;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  -moz-text-fill-color: transparent;
`;

const StyledBodyM = styled(HeadlineL).attrs({
  as: 'p',
  bold: true,
  brand25: true,
})`
  line-height: 1;
  &.rank-1 {
    --from-gradient-color: #ae5e13;
    --to-gradient-color: #fcff63;
    ${gradientText}
  }
  &.rank-2 {
    --from-gradient-color: #bcc8cb;
    --to-gradient-color: #99bdc8;
    ${gradientText}
  }
  &.rank-3 {
    --from-gradient-color: #cc8645;
    --to-gradient-color: #ffa463;
    ${gradientText}
  }
`;

const commonStyle = css`
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const firstStyle = css`
  background: var(--c-gradient-gold);
  -webkit-text-stroke: 0.5px #c07c26;
  ${commonStyle}
`;
const secondStyle = css`
  background: var(--c-gradient-silver);
  -webkit-text-stroke: 0.5px #757e80;
  ${commonStyle}
`;
const thirdStyle = css`
  background: var(--c-gradient-bronze);
  -webkit-text-stroke: 0.5px #ae6421;
  ${commonStyle}
`;

const Ranking = styled(BrandM)`
  &.rank-1 {
    ${firstStyle}
  }
  &.rank-2 {
    ${secondStyle}
  }
  &.rank-3 {
    ${thirdStyle}
  }
`;

const StyledSup = styled(LabelXS).attrs({ brand25: true, as: 'sup' })``;

const BrandSup = styled(BrandXS).attrs({ as: 'sup', bold: true })``;

type Props = WithRewardRange<object> & {
  scoreDistanceFromMyRanking?: number;
  useBrandFont?: boolean;
};

export const RankRewardTitle = ({
  startRank,
  endRank,
  startPct,
  endPct,
  scoreDistanceFromMyRanking,
  // TODO: remove when useNewAdvancedLobby is true for all users
  useBrandFont,
}: Props) => {
  const start = startRank ?? (startPct || 0);
  const end = endRank ?? (endPct || 0);

  const {
    flags: { useNewAdvancedLobby = false },
  } = useFeatureFlags();

  const RankingWrapper =
    useBrandFont || useNewAdvancedLobby ? Ranking : StyledBodyM;
  const Sup = useBrandFont || useNewAdvancedLobby ? BrandSup : StyledSup;

  return (
    <Vertical gap={0.5}>
      {start === end ? (
        <Horizontal>
          <TrophyIcon ranking={start} />
          <RankingWrapper className={`rank-${start}`}>
            <Ordinal ordinal={start} Sup={Sup} />
          </RankingWrapper>
        </Horizontal>
      ) : (
        <Horizontal gap={0.5}>
          <RankingWrapper>
            {startRank ? (
              <Ordinal ordinal={start} Sup={Sup} />
            ) : (
              <FormattedNumber value={start} style="percent" />
            )}
          </RankingWrapper>
          <RankingWrapper>-</RankingWrapper>
          <RankingWrapper>
            {endRank ? (
              <Ordinal ordinal={end} Sup={Sup} />
            ) : (
              <FormattedNumber value={end} style="percent" />
            )}
          </RankingWrapper>
        </Horizontal>
      )}
      {!!scoreDistanceFromMyRanking && (
        <LabelS>
          <LineupRewardsDistanceLabel
            distance={scoreDistanceFromMyRanking}
            withFromAppendix={false}
          />
        </LabelS>
      )}
    </Vertical>
  );
};
